import { useMemo, useState } from 'react';
import { SearchBarResult } from '../../Header/SearchBar/types';
import { OptionType } from '../../../utils/types';
import { fetchSearchUsersResults } from '../../../api/searchBarAPI';
import {
    acceptJoinRequestAPI,
    rejectJoinRequestAPI,
} from '../../../api/events';
import { useTranslation } from '../../../hooks/translations/TranslationProvider';
import { useSnackbar } from 'notistack';
import { createInvitationAPI } from '../../../api/invitations';

export const useEventRequests = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [resultsIsLoading, setResultsIsLoading] = useState(false);
    const [results, setResults] = useState<SearchBarResult>();
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const generateOptions = useMemo(() => {
        const userOptions: OptionType[] = [];
        results?.users?.forEach(user => {
            userOptions.push({
                id: user.id,
                label: user.username,
                image: user.avatarUrl,
                averageRating: user.averageRating,
            });
        });

        return { users: userOptions };
    }, [results]);

    const fetchResults = async (searchString: string) => {
        setError(null);
        try {
            setResultsIsLoading(true);
            const data = await fetchSearchUsersResults(searchString);
            setResults(data);
        } catch (error: any) {
            console.warn(error);
            setError(error);
        }
        setResultsIsLoading(false);
    };

    async function createInvitation(eventId: string, userId: string) {
        try {
            setIsLoading(true);
            await createInvitationAPI(eventId, userId);
            enqueueSnackbar('Invitation created successfully.', {
                variant: 'success',
            });
        } catch (error: any) {
            console.warn(error);
            if (error.response.status === 409) {
                setError(t('inviteUserDialog.alreadyInvitedError'));
            }
            if (error.response.status === 403) {
                setError(t('inviteUserDialog.cannotInviteYourselfError'));
            }
            setIsLoading(false);

            throw new Error(error);
        }
        setIsLoading(false);
    }

    const acceptJoinRequest = async (
        eventId: string,
        joinRequestId: string
    ) => {
        try {
            setIsLoading(true);
            await acceptJoinRequestAPI(eventId, joinRequestId);
        } catch (error: any) {
            console.warn(error);
            setError(error);
        }
        setIsLoading(false);
    };

    const rejectJoinRequest = async (
        eventId: string,
        joinRequestId: string
    ) => {
        try {
            setIsLoading(true);
            await rejectJoinRequestAPI(eventId, joinRequestId);
        } catch (error: any) {
            console.warn(error);
            setError(error);
        }
        setIsLoading(false);
    };

    return {
        isLoading,
        searchOptions: generateOptions,
        error,
        fetchResults,
        acceptJoinRequest,
        rejectJoinRequest,
        resultsIsLoading,
        createInvitation,
    };
};
